import { BBOXTURF, MapState } from "src/compentsModus/EmbeddedVectorMapEnhanced";
import { MitLayerHandleMaplibre } from "./MapFacadeMaplibre";
import { MapFacadeAbstract, MitMap } from "./MapFacade";
import { Localization, SettingsManager, Utils } from "@viamap/viamap2-common";
import { FilterType } from "src/states/ExploreSearchState";
import { ESInterface } from "src/compentsModus/ESInterface";
import * as M from 'maplibre-gl';
import { FeatureLayer } from "./WmsLayerFunc";
import { PropertyInformation } from "./PropertyInformation";
import { VectorLayer, isVectorLayer } from "./VectorLayerFunc";
import { POIS } from "src/HLED/managers/PoiManager";
import { AWSAPIGatewayInterface } from "./AWSAPIGatewayInterface";
import { MaplibreUtils } from "./MaplibreUtils";
import { GeojsonWFS, isGeojsonWFS } from "./WFSGeojsonLayerFunc";
import { PropertyInfoInterface } from "./PropertyInfoInterface";
import * as Turf from '@turf/turf';
import { DC } from "src/dataCollector/DataCollector";

export const FEATURELAYERTYPE_DYNAMIC = "DYNAMIC";

export function parseFeatureLayer(a: any): FeatureLayer | undefined {
    if (a instanceof FeatureLayer) {
        return a
    }
    if (a instanceof DynamicDataDrivenLayer) {
        return a
    }
    if (a instanceof VectorLayer) {
        return a
    }
    if (a == undefined) {
        return a
    }
    if (isVectorLayer(a)) {
        return new VectorLayer(a)
    }
    if (isGeojsonWFS(a)) {
        return new GeojsonWFS(a)
    }

    if (a.type) {
        switch (a.type) {
            case "dynamicdatalayer":
            case "dynamicdatalayerpropertyinfo":
                return new DynamicDataDrivenLayerPropertyInfo(a);
            case "dynamicdatalayerpoi":
                return new DynamicDataDrivenLayerPOI(a);
            case "dynamicdatalayercadasterinfo":
                return new DynamicDataDrivenLayerCadasterInfo(a);
        }
    }
    return new FeatureLayer(a);

}


export abstract class DynamicDataDrivenLayer extends FeatureLayer {
    customHandleMaplibre?: MitLayerHandleMaplibre

    constructor(layer: {}) {
        super(layer)
    }

    public override getType() {
        return "DYNAMIC" as const;
    }

    public getLegendURL() {
        return "";
    }

    abstract getDataForBBOX(bbox: BBOXTURF): Promise<any>;

    abstract transform2GeoJsonItem(data: any): any;

    public createGeoJsonSource(): M.GeoJSONSourceSpecification {
        return {
            type: "geojson",
            data: {
                "type": "FeatureCollection",
                "features": []
            }
        };
    }

    abstract createLayer(sourceId: string, layerId: string): any;

    onAdd(map: MapFacadeAbstract, newLocation: MapState): MitLayerHandleMaplibre {
        this.customHandleMaplibre = new MitLayerHandleMaplibre()
        let sourceId = "data-source-" + this.layer.label;
        this.customHandleMaplibre.addSource(sourceId, this.createGeoJsonSource())
        let layerId = "data-layer-" + this.layer.label;
        this.customHandleMaplibre.addLayer(this.createLayer(sourceId, layerId));
        map.addLayer(this.customHandleMaplibre)
        this.setDynStyling(map, {})
        this.getDataForBBOX(newLocation.bbox || []).then((a) => {
            if (this.customHandleMaplibre)
                map.setData(this.customHandleMaplibre, a || {})
        })

        return this.customHandleMaplibre
    }

    onRemove(map: MapFacadeAbstract) {
        let map2 = map.getMapPop()
        if (map2) {
            let layer = "data-layer-" + this.layer.label;
            let source = "data-source-" + this.layer.label;
            map2.getLayer(layer) && map2.removeLayer(layer);
            map2.getSource(source) && map2.removeSource(source);
        }
    }

    onMove(map: MapFacadeAbstract, newLocation: MapState) {
        const minZoom = this.layer.minZoom ?? 16

        if (newLocation && newLocation.zoom >= minZoom) {
            this.getDataForBBOX(newLocation.bbox || []).then((a) => {
                if (this.customHandleMaplibre)
                    map.setData(this.customHandleMaplibre, a || {})
            })

            return this.customHandleMaplibre
        }
        return this.customHandleMaplibre;
    }

    abstract setDynStyling(map: MapFacadeAbstract, value);

    public static formatHandelsInformation(item: any): string {
        let result = "Handlet ";
        if (item.handel_tidspunkt) {
            let d = new Date(item.handel_tidspunkt);
            result += d.toLocaleDateString() + "";
        }
        if (item.handel_samletKoebesum) {
            result += " til " + (item.handel_samletKoebesum / 1000000).toLocaleString(undefined, { maximumFractionDigits: 2 }) + " mio"
        }
        return result;
    }

    public static formatEjerInformation(item: any): string {
        if (item.sfe_registreretArealSamlet > 0 && item.sfe_registreretArealSamlet === item.sfe_vejarealSamlet) {
            return ""; // Vej
        }
        return [...new Set((item.ejf_ejere_liste || []).map((a) => {
            return a.navn
        }).filter((a) => a !== "ukendt ejertype").reduce((acc, curr, idx) => {
            if (idx < 2) {
                acc.push(curr);
            } else if (idx === 2) {
                acc.push("mfl.");
            }
            return acc;
        }, []))].join(", ");
    }
}

export class DynamicDataDrivenLayerPropertyInfo extends DynamicDataDrivenLayer {

    defaultValuesToShow: any = {};

    constructor(layer: any) {
        super(layer);
        this.defaultValuesToShow = { "data-to_show": layer.propertiesToDisplay || ["ejf_ejere"] };
    }

    async getDataForBBOX(bbox: BBOXTURF): Promise<any> {
        const elasticIndex = SettingsManager.getSystemSetting("indexNamePropertySearch");
        let bboxFilter = {
            "bfe_ejendomsType": { title: "Property Type", filterType: FilterType.SelectText, default: ["SamletFastEjendom"] },
            "tmp_bbox": { title: "Temporary bbox", filterType: FilterType.BoundingBox, default: [11.68, 55.06, 12.29, 55.42] }
        } as any;
        let bboxFilterValue = { "bfe_ejendomsType": ["SamletFastEjendom"], "tmp_bbox": bbox };
        let ifx = new ESInterface(elasticIndex);
        let items = await ifx.doSearch(bboxFilter, bboxFilterValue, SettingsManager.getSystemSetting("maxPropertiesFromElasticsearch"))
            .catch((err) => {
                alert("Got error:" + (err.message || err))
            })
        return (this.transform2GeoJsonItem(items));
    }

    transform2GeoJsonItem(data: any): any {

        return data ? {
            "type": "FeatureCollection",
            "features": data.map((item: any) => {
                let format_handel_info = DynamicDataDrivenLayer.formatHandelsInformation(item);
                item.ejf_ejere = DynamicDataDrivenLayer.formatEjerInformation(item);
                return {
                    type: "Feature",
                    properties: {
                        ...item, ...formatESInterfaceData(item as ESInterfaceData), format_handel_info: format_handel_info
                    },
                    geometry: {
                        "type": "Point",
                        "coordinates": [item.koord_lng, item.koord_lat]
                    }
                }
            })
        } : {};
    }

    createLayer(sourceId: string, layerId: string): any {
        return {
            id: layerId,
            type: 'symbol',
            source: sourceId,
            layout: {
                "text-field": this.createTextFieldSpec(this.defaultValuesToShow),
                'text-font': ["Noto Sans Bold"],
                'text-size': 12,
                'text-allow-overlap': false
            },
            paint: {
                'text-color': '#454659',
                'text-halo-color': "#fffd",
                'text-halo-width': 1.5,
                'text-halo-blur': 0,
            },
            minzoom: this.layer.minZoom
        }
    }

    public createTextFieldSpec(value: { "data-to_show": string[] }): any {
        const values = value?.["data-to_show"] ?? this.layer.variantValues?.["data-to_show"] ?? this.layer?.variantSelectors?.["data-to_show"]?.default ?? []
        let x = values.flatMap((a) => [["concat", ["get", a], ["case", ["has", a], "\n", ""]], { "font-scale": 1.0 }])
        let y = ["format", ...x]
        return y;
    }

    setDynStyling(map: MapFacadeAbstract, value) {
        if (value) {
            this.layer.variantValues = { ...this.layer.variantValues, ...value }
        }

        let y = this.createTextFieldSpec(value);

        let custom = this.customHandleMaplibre
        if (custom)
            map.setLayoutProperty(custom, { "text-field": y })
    }
}

export class DynamicDataDrivenLayerPOI extends DynamicDataDrivenLayer {
    setDynStyling() {
        throw new Error("Method not implemented.");
    }
    private poiTypes: string[];

    /**
     * 
     * @param layer An object {..., layers:string,  }. layers must be a JSON-string of poi types. E.g. '["stop*,"doctor"]'
     */
    constructor(layer: any) {
        super({ path: "dummy", label: "dummy", host: "dummy", ...layer });
        try {
            this.poiTypes = JSON.parse(layer.layers);
        } catch (err: any) {
            throw new Error("Error parsing poiTypes: " + (err.message || err));
        }
    }

    async getDataForBBOX(bbox: BBOXTURF): Promise<any> {
        let serviceUrl = SettingsManager.getSystemSetting("poiServiceURL", "https://viamap.net/getpoi/");
        let bbox2 = Utils.formatString("{lat},{lng}%20{lat2},{lng2}", { lat: bbox[1], lng: bbox[0], lat2: bbox[3], lng2: bbox[2] });

        // Convert POIList to comma separated string
        let poiTypesString: string = ""; // Example "metro,train,strain,hospital,doctor,school,supermarket";
        this.poiTypes.forEach((poiType: string) => {
            if (poiTypesString !== "") {
                poiTypesString += ",";
            }
            poiTypesString += poiType;
        }
        );

        let token = SettingsManager.getSystemSetting("viamapAPIToken");
        let query = serviceUrl + "?bbox=" + bbox2 + "&poitypes=" + poiTypesString + "&token=" + token;
        try {
            let data: { [poitype: string]: any } = {};
            if (poiTypesString == "") {
                for (const poitype of this.poiTypes) {
                    data[poitype] = { numfound: 0, POIs: [] }
                }
            } else if (SettingsManager.getSystemSetting("useMapitPOIServer", false)) {
                // Call Mapit POI service
                data = await AWSAPIGatewayInterface.getPois(bbox2, poiTypesString);
            } else {
                const response = await fetch(query);
                data = await response.json();
            }

            return (this.transform2GeoJsonItem(data));
        } catch (err: any) {
            throw new Error("Got error:" + (err.message || err))
        }
    }

    transform2GeoJsonItem(data: any): any {
        let featuresArray: any[] = [];
        // Process response
        Object.keys(data).forEach((key) => {
            let val = data[key];
            if (val.status && val.status === "none found") {
                // Nothing to display
            } else {
                let icon = SettingsManager.getSystemSetting("poiPinPrefix", "") + POIS[key].icon;
                let label = POIS[key].name;
                if (val && val.POIs && Array.isArray(val.POIs)) {
                    val.POIs.forEach((poi: any) => {
                        featuresArray.push({
                            type: "Feature",
                            properties: {
                                icon: icon,
                                label: label,
                                poiName: poi.name,
                                key: key
                            },
                            geometry: {
                                "type": "Point",
                                "coordinates": [poi.poilatlng[1], poi.poilatlng[0]]
                            }
                        }
                        );
                    });
                }
            }
        });

        return {
            "type": "FeatureCollection",
            "features": featuresArray
        };

    }

    createLayer(sourceId: string, layerId: string): any {
        return MaplibreUtils.makeLayerPoi(layerId, sourceId)
    }
}


export class DynamicDataDrivenLayerCadasterInfo extends DynamicDataDrivenLayer {

    defaultValuesToShow: any = {};
    dataCacheByBFEnr: { [bfeNr: string]: any } = {};

    constructor(layer: any) {
        super(layer);
        this.defaultValuesToShow = { "data-to_show": layer.propertiesToDisplay || ["ejf_ejere"] };
    }

    async getDataForBBOX(bbox: BBOXTURF): Promise<any> {
        let bPoly = Turf.bboxPolygon(bbox as [number, number, number, number]);
        //        let resMatr = await PropertyInfoInterface.fetchMat2CadasterWFSforPolygonPOST(bPoly.geometry);
        let resMatr = await PropertyInfoInterface.fetchMat2CadasterWFSforPolygonFilterIntersectsPost(bPoly.geometry);

        let cadasters: any[] = [];

        for (let matr of resMatr.features) {
            let sfeNr = matr.properties["mat:samletFastEjendomLokalId"];
            let centroid = Turf.centroid(matr as any);
            if (this.dataCacheByBFEnr[sfeNr]) {
                cadasters.push(
                    {
                        ...this.dataCacheByBFEnr[sfeNr],
                        koord_lng: centroid.geometry.coordinates[0],
                        koord_lat: centroid.geometry.coordinates[1]
                    });
                continue;
            }
            let ejere = await DC.DLL.EjendomEjerskab("" + sfeNr);
            let cad = {
                ...matr.properties,
                jords_matrikelnr: matr.properties["mat:matrikelnr"],
                jords_ejerlavskode: matr.properties["mat:ejerlavskode"],
                ejf_ejere_liste: ejere,
                sfe_nr: sfeNr,
                koord_lng: centroid.geometry.coordinates[0],
                koord_lat: centroid.geometry.coordinates[1]
            }
            this.dataCacheByBFEnr[sfeNr] = cad;
            cadasters.push(cad);
        }

        let result = this.transform2GeoJsonItem(cadasters);
        return (result);
    }

    transform2GeoJsonItem(data: any): any {

        return data ? {
            "type": "FeatureCollection",
            "features": data.map((item: any) => {
                let format_handel_info = DynamicDataDrivenLayer.formatHandelsInformation(item);
                item.ejf_ejere = DynamicDataDrivenLayer.formatEjerInformation(item);
                return {
                    type: "Feature",
                    properties: {
                        ...item, ...formatESInterfaceData(item as ESInterfaceData), format_handel_info: format_handel_info
                    },
                    geometry: {
                        "type": "Point",
                        "coordinates": [item.koord_lng, item.koord_lat]
                    }
                }
            })
        } : {};
    }

    createLayer(sourceId: string, layerId: string): any {
        return {
            id: layerId,
            type: 'symbol',
            source: sourceId,
            layout: {
                "text-field": this.createTextFieldSpec(this.defaultValuesToShow),
                'text-font': ["Noto Sans Bold"],
                'text-size': 11,
                'text-allow-overlap': false
            },
            paint: {
                'text-color': '#454659',
                'text-halo-color': "#fffd",
                'text-halo-width': 1.5,
                'text-halo-blur': 0,
            },
            minzoom: this.layer.minZoom
        }
    }

    createTextFieldSpec(value: { "data-to_show": string[] }): any {
        const values = value?.["data-to_show"] ?? this.layer.variantValues?.["data-to_show"] ?? this.layer?.variantSelectors?.["data-to_show"]?.default ?? []
        let x = values.flatMap((a) => [["concat", ["get", a], ["case", ["has", a], "\n", ""]], { "font-scale": 1.0 }])
        let y = ["format", ...x]
        return y;
    }

    setDynStyling(map: MapFacadeAbstract, value) {
        if (value) {
            this.layer.variantValues = { ...this.layer.variantValues, ...value }
        }

        let y = this.createTextFieldSpec(value);

        let custom = this.customHandleMaplibre
        if (custom)
            map.setLayoutProperty(custom, { "text-field": y })
    }
}


// For debugging purposes only - remove when done
export class DynamicDataDrivenLayerPropertyInfoDebug extends DynamicDataDrivenLayer {

    defaultValuesToShow: any = {};

    constructor(layer: any) {
        super(layer);
        this.defaultValuesToShow = { "data-to_show": layer.propertiesToDisplay || ["ejf_ejere"] };
    }

    async getDataForBBOX(bbox: BBOXTURF): Promise<any> {
        let bPoly = Turf.bboxPolygon(bbox as [number, number, number, number]);
        let resMatr = await PropertyInfoInterface.fetchMat2CadasterWFSforPolygonFilterIntersectsPost(bPoly.geometry);

        // let dataCacheByBFEnr: {[bfeNr: string]: any} = {};
        // let cadasters:any[] = [];

        // for (let matr of resMatr.features) {
        //     let sfeNr = matr.properties["mat:samletFastEjendomLokalId"];
        //     if (dataCacheByBFEnr[sfeNr]) {
        //         cadasters.push(dataCacheByBFEnr[sfeNr]);
        //         continue;
        //     }
        //     let centroid = Turf.centroid(matr as any);
        //     let ejere = await PropertyInfoInterface.getEjerskab(""+sfeNr);
        //     let cad = {
        //         ...matr.properties,
        //         sfe_registreretArealSamlet: "item.sfe_registreretArealSamlet",
        //         sfe_vejarealSamlet: "item.sfe_vejarealSamlet",
        //         ejf_ejere_liste : ejere,
        //         sfe_nr: sfeNr,
        //         koord_lng: centroid.geometry.coordinates[0],
        //         koord_lat: centroid.geometry.coordinates[1],
        //         handel_tidspunkt: "item.handel_tidspunkt",
        //         handel_samletKoebesum: "item.handel_samletKoebesum",
        //     }
        //     dataCacheByBFEnr[sfeNr] = cad;
        //     cadasters.push(cad);
        // }

        // let result = this.transform2GeoJsonItem(cadasters);
        // return (result);
        return resMatr;
    }

    transform2GeoJsonItem(data: any): any {

        function formatHandelsInformation(item: any): string {
            let result = "Handlet ";
            if (item.handel_tidspunkt) {
                let d = new Date(item.handel_tidspunkt);
                result += d.toLocaleDateString() + "";
            }
            if (item.handel_samletKoebesum) {
                result += " til " + (item.handel_samletKoebesum / 1000000).toLocaleString(undefined, { maximumFractionDigits: 2 }) + " mio"
            }
            return result;
        }
        function formatEjerInformation(item: any): string {
            if (item.sfe_registreretArealSamlet > 0 && item.sfe_registreretArealSamlet === item.sfe_vejarealSamlet) {
                return ""; // Vej
            }
            return [...new Set((item.ejf_ejere_liste || []).map((a) => {
                return a.navn
            }).filter((a) => a !== "ukendt ejertype"))].join(",  ");
        }
        return data ? {
            "type": "FeatureCollection",
            "features": data.map((item: any) => {
                let format_handel_info = formatHandelsInformation(item);
                item.ejf_ejere = formatEjerInformation(item);
                return {
                    type: "Feature",
                    properties: {
                        ...item, ...formatESInterfaceData(item as ESInterfaceData), format_handel_info: format_handel_info
                    },
                    geometry: {
                        "type": "Point",
                        "coordinates": [item.koord_lng, item.koord_lat]
                    }
                }
            })
        } : {};
    }

    createLayer(sourceId: string, layerId: string): any {
        // return {
        //     id: layerId,
        //     type: 'symbol',
        //     source: sourceId,
        //     layout: {
        //         "text-field": this.createTextFieldSpec(this.defaultValuesToShow),
        //         'text-font': ["Noto Sans Bold"],
        //         'text-size': 12,
        //         'text-allow-overlap': false
        //     },
        //     paint: {
        //         'text-color': '#454659',
        //         'text-halo-color': "#fffd",
        //         'text-halo-width': 1.5,
        //         'text-halo-blur': 0,
        //     },
        //     minzoom: this.layer.minZoom
        // }
        return ({
            id: layerId,
            type: "fill",
            source: sourceId,
            layout: {
            },
            'paint': {
                'fill-color': "blue",
                "fill-outline-color": "grey",
            }
        });
    }

    createTextFieldSpec(value: { "data-to_show": string[] }): any {
        const values = value?.["data-to_show"] ?? this.layer.variantValues?.["data-to_show"] ?? this.layer?.variantSelectors?.["data-to_show"]?.default ?? []
        let x = values.flatMap((a) => [["concat", ["get", a], ["case", ["has", a], "\n", ""]], { "font-scale": 1.0 }])
        let y = ["format", ...x]
        return y;
    }

    setDynStyling(map: MapFacadeAbstract, value) {
        if (value) {
            this.layer.variantValues = { ...this.layer.variantValues, ...value }
        }

        let y = this.createTextFieldSpec(value);

        let custom = this.customHandleMaplibre
        if (custom)
            map.setLayoutProperty(custom, { "text-field": y })
    }
}

export class DynamicDataDrivenLayerFunc {
    public async OnMoveHandler(map: MitMap, onMoveHandler: (map: MitMap, newLocation: MapState) => any) {
        function getBbox(map: MitMap): BBOXTURF {
            let bounds: M.LngLatBounds = map.getBounds();
            let bboxMap: any = bounds.toArray();
            let bbox = [...bboxMap[0], ...bboxMap[1]];
            return bbox;
        }

        let bbox = getBbox(map);
        let mapState = {
            bearing: map.getBearing(),
            pitch: map.getPitch(),
            zoom: map.getZoom(),
            center: (map.getCenter().get2dArr()),
            bbox: bbox
        }
        onMoveHandler(map, mapState);
    }
}




function formatESInterfaceData(item: ESInterfaceData): Partial<Record<string, any>> {
    return {
        handel_samletKoebesum_valutakode: item?.handel_samletKoebesum ? formatCurrency(item.handel_samletKoebesum, item?.handel_valutaKode) : Localization.getText("Not available"),
        handel_valutaKode: item?.handel_valutaKode,
        handel_tidspunkt: item?.handel_tidspunkt && formatDate(item.handel_tidspunkt),
        handel_maade: item?.handel_maade,
        bbr_anvendelse: item?.bbr_anvendelse && PropertyInformation.bygningsAnvendelsesTekst(item.bbr_anvendelse) + ` (${item.bbr_anvendelse})`,
        sfe_registreretArealSamlet: item?.sfe_registreretArealSamlet && formatArea(item?.sfe_registreretArealSamlet)
    }
}


function formatCurrency(a: string | number, currency: string) {
    if (typeof a == "string") {
        return (parseFloat(a) / 1000000).toLocaleString(Localization.getLocale(), { maximumFractionDigits: 2 }) + " Mio." + (currency ? " " + currency : "");
    }
    return (a / 1000000).toLocaleString(Localization.getLocale(), { maximumFractionDigits: 2 }) + " Mio." + (currency ? " " + currency : "");
}

function formatDate(a: string | number) {
    return new Date(a).toLocaleDateString(Localization.getLocale(), { year: "numeric", month: "2-digit", day: "2-digit" });
}

function formatArea(a: string | number) {
    return parseInt(a.toString()).toLocaleString(Localization.getLocale()) + " m2"
}

type ESInterfaceData = {
    handel_samletKoebesum: number
    handel_valutaKode: string,
    "id_namespace": string,
    "id_lokalId": string,
    "bfe_nr": string,
    "bfe_ejendomsType": string,
    "bfe_registreringFra": string,
    "bfe_virkningFra": string,
    "bfe_adresse": string,
    "koord_status": string,
    "koord_lat": number,
    "koord_lng": number,
    "koord_x": number,
    "koord_y": number,
    "geopoint": string,
    "bygningspunkt_DDKNcelle10km": string,
    "bygningspunkt_DDKNcelle1km": string,
    "bygningspunkt_DDKNcelle100m": string,
    "zone_zone": 1,
    "zone_status": string,
    "jords_matrikelnr": string,
    "jords_ejerlavskode": string,
    "sfe_registreretArealSamlet": number,
    "sfe_vejarealSamlet": number,
    "ejf_postnr": string,
    "ejf_kommunenr": string,
    "ejf_ejere_liste": [
        {
            "navn": string,
            "adresse": string,
            "postnr": string,
            "postdistrikt": string,
            "landekode": string,
            "CVRNummer": number,
            "andelProcent": number,
            "displayText": string
        }
    ],
    "ejf_afdoed": boolean,
    "ejf_cvrnr": number,
    "ejf_ejere": string,
    "ejf_ejereLang": string,
    "ejf_reklameBeskyttelse": boolean,
    "bbr_anvendelse": string,
    "bbr_opfoerelsesAAr": number,
    "bbr_tilbygningsAAr": number,
    "bbr_bebyggetAreal": number,
    "bbr_samletBygningsAreal": number,
    "bbr_antalEtager": 5,
    "bbr_bygningensSamledeBoligAreal": number,
    "bbr_koordinater": string,
    "bbr_status": string,
    "bbr_yderVaegMateriale": string,
    "bbr_tagMateriale": string,
    "bbr_samletErhvervsAreal": number,
    "bbr_varmeInstallation": string,
    "poi_dist_junction": number,
    "poi_dist_chargingstation": number,
    "poi_dist_supercharger": number,
    "poi_dist_stop": number,
    "poi_dist_train": number,
    "trafik_highest_hdt": number,
    "trafik_highest_trucks": number,
    "trafik_highest_dist": number,
    "trafik_highest_place": string,
    "trafik_highest_truckRatio": number,
    "handel_tidspunkt": string,
    "handel_forretningsh": null,
    "handel_maade": string,
    "handel_status": string,
    "import_batch_number": number
}

